/**
=========================================================
* E4A-IoT-Manager React Frontend Dashboard 
* Copyright © 2024 Electronics4All Inc.
=========================================================
*/

// @mui material components
import Card from "@mui/material/Card";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import EditTwoToneIcon from "@mui/icons-material/EditTwoTone";
import DeleteTwoToneIcon from "@mui/icons-material/DeleteTwoTone";


// Material Dashboard React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { useEffect, useState, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// Permissions
import { AbilityContext } from "Can";
import { useAbility } from "@casl/react";
import { AuthContext } from "context";

// E4A React components
import DashboardLayout from "e4acomponents/LayoutContainers/DashboardLayout";
import DashboardNavbar from "e4acomponents/Navbars/DashboardNavbar";
import Footer from "e4acomponents/Footer";
import DataTable from "e4acomponents/Tables/DataTable";
import ConfirmDialog from "e4acomponents/ConfirmDialog";

// Data
import partCategoryService from "services/part-category-service";

function PartCodeView() {

  // Data
  const [partCategories, setpartCategories] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  // Permissions
  const ability = useAbility(AbilityContext);
  const authContext = useContext(AuthContext);

  const [openDialog, setOpenDialog] = useState(false);
  const [dialogProps, setDialogProps] = useState({
    title: "Delete (Archive) Part Category ?",
    prompt: "Are you sure you want to delete (archive) this part category?",
    onClose: () => { }
  })

  // Fetch part codes
  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const response = await partCategoryService.getAllPartCategories();
      setpartCategories(response.data.attributes);
    })();
  }, []);

  useEffect(() => {
    setTableData(getRows(partCategories));
  }, [partCategories]);

  // Format data for DataTable
  const getRows = (info) => {
    let updatedInfo = info.map((element) => {
      return {
        categoryCode: "E4A-" + element.categoryCode + "-#####-#",
        parentCategory: element.parentCategory,
        description: element.description,
        examples: element.examples,
        status: element.status,
        id: element._id
      };
    });
    return updatedInfo
  }

  const clickEditHandler = (id) => {
    navigate(`/part-code-management/edit-part-category/${id}`);
  };

  const clickDeleteHandler = async (id) => {
    try {
      const payload = {
        data: {
          attributes: {
            status: "Archived"
          },
          logs: {
            user: await authContext.getCurrentUser(),
            comment: ""
          }
        }
      }
      await partCategoryService.editStatus(id, payload);
      setpartCategories((prev) => prev.filter((item) => item._id !== id));
    } catch (error) {
      console.error("Error deleting part category:", error);
    }
  };

  const handleCloseDialog = async (response, title, id) => {
    if (response) {
      await clickDeleteHandler(id);
    }
    setOpenDialog(false);
  }

  const handleOpenDialog = (id) => {
    setDialogProps({
      ...dialogProps,
      onClose: handleCloseDialog,
      id: id
    })
    setOpenDialog(true);
  }


  // Array containing the dataTable's headers and rows
  const dataTableData = {
    columns: [
      { Header: "Part Category", accessor: "categoryCode", width: "20%" },
      { Header: "Parent Category", accessor: "parentCategory", width: "20%" },
      { Header: "Description", accessor: "description", width: "20%" },
      { Header: "Example and Notes", accessor: "examples", width: "30%" },

      ...(ability.can("access", "edit-part-category") ? [{
        Header: "Actions",
        disableSortBy: true,
        accessor: "actions",
        Cell: (info) => {
          return (
            <MDBox display="flex" alignItems="center">

              {/* Delete Part Category Icon */}
              <Tooltip
                title="Delete Part"
                onClick={() => handleOpenDialog(info.cell.row.original.id)}
              >
                <IconButton>
                  <DeleteTwoToneIcon color="error" />
                </IconButton>
              </Tooltip>

              {/* Edit Part Category Icon */}
              <Tooltip
                title="Edit Part"
                onClick={() => clickEditHandler(info.cell.row.original.id)}
              >
                <IconButton>
                  <EditTwoToneIcon color="success" />
                </IconButton>
              </Tooltip>

            </MDBox>
          );
        },
      }] : []),
    ],
    rows: tableData
  };

  // Stop loading when data is fetched
  useEffect(() => {
    if (isLoading && tableData.length > 0) {
      setIsLoading(false);
    }
  }, [tableData]);

  // Behaviour when clicking the create partcode button
  const clickCreateHandler = () => {
    navigate("/part-code-management/create-part-category");
  };


  return (
    <DashboardLayout>

      <ConfirmDialog
        open={openDialog}
        props={dialogProps}
      />

      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Card>
          <MDBox p={3} lineHeight={1} display="flex" justifyContent="space-between">
            <MDTypography variant="h5" fontWeight="medium">
              Part Categories list
            </MDTypography>
            {ability.can("access", "create-part-category") && (
              <MDButton
                variant="gradient"
                color="info"
                size="small"
                type="submit"
                onClick={clickCreateHandler}
              >
                + Create Part Category
              </MDButton>
            )}
          </MDBox>
          <MDBox>
            <DataTable
              table={dataTableData}
              canSearch
              customRowProps={(row, index) => ({
                style: { '--row-index': index }
              })}
              isLoading={isLoading}
              tableId="part-categories-table"
            />
          </MDBox>
        </Card>
      </MDBox>
      <MDBox>
        <Footer />
      </MDBox>

    </DashboardLayout>
  );
}

export default PartCodeView;
